import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClientAnalyticsMediaOutletOption, ClientAnalyticsAuthorOption, ClientAnalyticsControllerService, CreateSavedAnalyticsViewRequest } from 'api-clipping';
import { take } from 'rxjs';
import { DateRangePickerComponent, DateRange, CompareOption, formatDisplayDate } from '../date-range-picker/date-range-picker.component';
import { SOURCE_TYPES, INDUSTRY_TOPICS, CORPORATE_TOPICS } from '../../../../shared/interfaces/taxonomy-data';
import { buildAnalyticsFilterRequest, MEDIA_TYPE_TO_BACKEND } from '../shared/analytics-request.util';
import { serializeFilterState, isRelativeDatePreset } from '../shared/saved-view.util';
import { translateSourceType, translateTopic } from '../shared/taxonomy.util';

// Maps AnalyticsPanelComponent's tab ids to the backend's AnalyticsScreen enum - only these 7
// screens can have a Saved View; 'reports'/'saved_views'/'exports' are not analytics screens and
// hide the Save View button entirely (see isSaveableScreen below).
const SCREEN_TO_BACKEND: Record<string, CreateSavedAnalyticsViewRequest.ScreenEnum> = {
	overview: 'OVERVIEW',
	coverage_dashboard: 'COVERAGE_DASHBOARD',
	coverage_analysis: 'COVERAGE_ANALYSIS',
	brands: 'BRANDS',
	topics: 'TOPICS',
	sentiment: 'SENTIMENT',
	media: 'MEDIA',
};

export interface FilterState {
	dateRange: string;
	comparePeriod: string;
	startDate?: Date;
	endDate?: Date;
	// Which DateRangePickerComponent quick preset (if any) produced startDate/endDate -
	// undefined/'custom' means the user picked literal dates by hand. Saved Views uses this to
	// tell a "relative" period (recomputed from today on every open) from a "fixed" one.
	datePresetKey?: string;
	compareType?: 'previous_period' | 'same_period_last_year' | 'custom' | 'none';
	compareStartDate?: Date;
	compareEndDate?: Date;
	brands: string[];
	brandRole: 'all' | 'client' | 'competition';
	mediaTypes: string[];
	mediaOutlets: string[];
	authors: string[];
	topicGroup: 'all' | 'Industry' | 'Corporate';
	topics: string[];
	sourceTypes: string[];
	sentiments: string[];
}

// The brand quick-select and the KPI/trend/brand-list math all need "today's" quarter, not a
// fixed sample date - same "today"-relative rule the Filters drawer's own date picker
// (DateRangePickerComponent) now follows for its own quick presets.
function currentQuarterRange(reference: Date = new Date()): { start: Date; end: Date } {
	const quarterStartMonth = Math.floor(reference.getMonth() / 3) * 3;
	const start = new Date(reference.getFullYear(), quarterStartMonth, 1);
	const end = new Date(reference.getFullYear(), quarterStartMonth + 3, 0);
	return { start, end };
}

function previousQuarterRange(reference: Date = new Date()): { start: Date; end: Date } {
	const { start: thisQuarterStart } = currentQuarterRange(reference);
	const end = new Date(thisQuarterStart.getTime() - 24 * 60 * 60 * 1000);
	const start = new Date(end.getFullYear(), Math.floor(end.getMonth() / 3) * 3, 1);
	return { start, end };
}

// Default filters: current quarter vs previous quarter, every brand tag selected (populated once
// AnalyticsPanelComponent loads the client's real brand tags - see its loadBrandTags()). Computed
// fresh (not a module-level constant) so "today" never goes stale across a long-lived tab.
// lang/thisQuarterLabel/vsLabel: this is a free function (no DI), so AnalyticsPanelComponent's own
// field initializer passes its TranslateService-resolved strings in; the English defaults here
// only matter if some future caller forgets to.
export function createDefaultFilterState(lang?: string, thisQuarterLabel = 'This quarter', vsLabel = 'vs'): FilterState {
	const { start, end } = currentQuarterRange();
	const { start: prevStart, end: prevEnd } = previousQuarterRange();
	return {
		dateRange: `${thisQuarterLabel} (${formatDisplayDate(start, lang)} – ${formatDisplayDate(end, lang)})`,
		comparePeriod: `${vsLabel} ${formatDisplayDate(prevStart, lang)} – ${formatDisplayDate(prevEnd, lang)}`,
		startDate: start,
		endDate: end,
		datePresetKey: 'this_quarter',
		compareType: 'previous_period',
		compareStartDate: prevStart,
		compareEndDate: prevEnd,
		brands: [],
		brandRole: 'all',
		mediaTypes: [],
		mediaOutlets: [],
		authors: [],
		topicGroup: 'all',
		topics: [],
		sourceTypes: [],
		sentiments: [],
	};
}

export interface BrandOption {
	id: string;
	name: string;
	role: 'client' | 'competition';
	color: string;
}

// Labels come from translateSourceType/sentiment getters below - these keep only the stable
// id (the filter VALUE, matched against the backend) and icon.
const MEDIA_TYPE_IDS: { id: string; icon: string }[] = [
	{ id: 'Web', icon: 'public' },
	{ id: 'TV', icon: 'tv' },
	{ id: 'Radio', icon: 'radio' },
	{ id: 'Print', icon: 'newspaper' },
	{ id: 'Social', icon: 'share' },
];

const SENTIMENT_IDS: { id: string; icon: string }[] = [
	{ id: 'Positive', icon: 'sentiment_satisfied' },
	{ id: 'Neutral', icon: 'sentiment_neutral' },
	{ id: 'Negative', icon: 'sentiment_dissatisfied' },
];

// Ported from analytics-design/src/components/FilterBar.tsx. The Export button is intentionally
// non-functional in this step (no dropdown, no click handler) - that's explicit scope for now.
@Component({
	selector: 'app-filter-bar',
	standalone: true,
	imports: [MatIconModule, FormsModule, TranslateModule, DateRangePickerComponent],
	templateUrl: './filter-bar.component.html',
	styleUrl: './filter-bar.component.scss',
})
export class FilterBarComponent implements OnChanges {
	@Input() filters: FilterState = createDefaultFilterState();
	// Populated by AnalyticsPanelComponent from GET /v1/client/analytics/brands - this client's
	// real Company/Competitor tags, replacing the old hardcoded 6-brand sample list.
	@Input() brandOptions: BrandOption[] = [];
	// Populated by AnalyticsPanelComponent from GET /v1/client/analytics/filter-options - this
	// client's real media outlets/authors, replacing the drawer's old "coming soon" placeholders.
	@Input() mediaOutletOptions: ClientAnalyticsMediaOutletOption[] = [];
	@Input() authorOptions: ClientAnalyticsAuthorOption[] = [];
	// Which screen (AnalyticsPanelComponent's activeTab) and, for Coverage Analysis, which of its
	// three tabs - what a Saved View created right now would be opened back into.
	@Input() screen = '';
	@Input() coverageAnalysisTab?: string;
	// Driven by AnalyticsPanelComponent, which owns the html2canvas+jsPDF snapshot work (it needs
	// a DOM reference to the screen content, which this component doesn't have) - this input only
	// reflects that work's in-flight state back onto the button.
	@Input() pdfExporting = false;
	@Output() filtersChange = new EventEmitter<FilterState>();
	@Output() exportPdfRequested = new EventEmitter<void>();

	mediaOutletSearch = '';
	authorSearch = '';

	constructor(
		private readonly api: ClientAnalyticsControllerService,
		private readonly translate: TranslateService,
	) {}

	get isSaveableScreen(): boolean {
		return !!SCREEN_TO_BACKEND[this.screen];
	}

	// --- Save View modal ---

	saveViewModalOpen = false;
	saveViewName = '';
	saveViewPeriodType: 'FIXED' | 'RELATIVE' = 'FIXED';
	saveViewLoading = false;
	toastMessage: string | null = null;
	private toastTimer?: ReturnType<typeof setTimeout>;

	get canSaveRelativePeriod(): boolean {
		return isRelativeDatePreset(this.currentFilters.datePresetKey);
	}

	openSaveViewModal(): void {
		this.saveViewName = '';
		this.saveViewPeriodType = this.canSaveRelativePeriod ? 'RELATIVE' : 'FIXED';
		this.saveViewModalOpen = true;
	}

	closeSaveViewModal(): void {
		this.saveViewModalOpen = false;
	}

	private triggerToast(msg: string): void {
		this.toastMessage = msg;
		clearTimeout(this.toastTimer);
		this.toastTimer = setTimeout(() => (this.toastMessage = null), 3000);
	}

	submitSaveView(): void {
		const name = this.saveViewName.trim();
		const screen = SCREEN_TO_BACKEND[this.screen];
		if (!name || !screen) return;

		const request: CreateSavedAnalyticsViewRequest = {
			name,
			screen,
			coverageAnalysisTab: screen === 'COVERAGE_ANALYSIS' ? this.coverageAnalysisTab : undefined,
			filtersJson: serializeFilterState(this.currentFilters, this.saveViewPeriodType),
		};

		this.saveViewLoading = true;
		this.api
			.createSavedAnalyticsView(request)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.saveViewLoading = false;
					this.saveViewModalOpen = false;
					this.triggerToast(this.translate.instant('ANALYTICS_FILTER_BAR.VIEW_SAVED_TOAST', { name }));
				},
				error: () => {
					this.saveViewLoading = false;
					this.triggerToast(this.translate.instant('ANALYTICS_FILTER_BAR.FAILED_TO_SAVE'));
				},
			});
	}

	// --- Export (PDF snapshot of the open screen) ---

	requestPdfExport(): void {
		this.exportPdfRequested.emit();
	}

	// --- Export mentions (.xlsx) - secondary action now; the primary "Export" button produces a
	// PDF of the open screen instead (see requestPdfExport above). ---

	exportLoading = false;

	exportMentions(): void {
		const fields = buildAnalyticsFilterRequest(this.currentFilters);
		if (!fields) return;

		this.exportLoading = true;
		this.api
			.createMentionsExport(fields as any)
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					const blob = new Blob([response as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, '_blank');
					this.exportLoading = false;
				},
				error: (err: HttpErrorResponse) => {
					this.exportLoading = false;
					// Blob body regardless of status - 409 (row limit) and now 400 (startDate/endDate
					// validation, docs/features/analitika/2026-10-08-bezbednosen-pregled.md
					// recommendation 4) both arrive this way, since mainInterceptor forces
					// responseType: 'blob' for any '/export' URL, success or error alike.
					if (err.error instanceof Blob) {
						err.error.text().then((text: string) => {
							let message = this.translate.instant('ANALYTICS_FILTER_BAR.EXPORT_ERROR_DEFAULT');
							try {
								message = JSON.parse(text).message || message;
							} catch {
								/* keep default message */
							}
							this.triggerToast(message);
						});
					} else {
						this.triggerToast(this.translate.instant('ANALYTICS_FILTER_BAR.FAILED_TO_EXPORT'));
					}
				},
			});
	}

	get allBrands(): BrandOption[] {
		return this.brandOptions;
	}

	readonly industryTopics = INDUSTRY_TOPICS;
	readonly corporateTopics = CORPORATE_TOPICS;
	readonly sourceTypes = SOURCE_TYPES;

	get mediaTypeOptions(): { id: string; label: string; icon: string }[] {
		const labels: Record<string, string> = {
			Web: this.translate.instant('ANALYTICS_FILTER_BAR.MEDIA_TYPE_WEB'),
			TV: this.translate.instant('ANALYTICS_FILTER_BAR.MEDIA_TYPE_TV'),
			Radio: this.translate.instant('ANALYTICS_FILTER_BAR.MEDIA_TYPE_RADIO'),
			Print: this.translate.instant('ANALYTICS_FILTER_BAR.MEDIA_TYPE_PRINT'),
			Social: this.translate.instant('ANALYTICS_FILTER_BAR.MEDIA_TYPE_SOCIAL'),
		};
		return MEDIA_TYPE_IDS.map((m) => ({ ...m, label: labels[m.id] }));
	}

	get sentimentOptions(): { id: string; label: string; icon: string }[] {
		const labels: Record<string, string> = {
			Positive: this.translate.instant('CLIPS.SENTIMENT_RATING_TYPE.POSITIVE'),
			Neutral: this.translate.instant('CLIPS.SENTIMENT_RATING_TYPE.NEUTRAL'),
			Negative: this.translate.instant('CLIPS.SENTIMENT_RATING_TYPE.NEGATIVE'),
		};
		return SENTIMENT_IDS.map((s) => ({ ...s, label: labels[s.id] }));
	}

	// Display-only translation for the drawer's Topic/Source Type chip lists - the underlying
	// filter VALUE stays the raw English AiTaxonomy label (see taxonomy.util.ts).
	translateTopicLabel(topic: string): string {
		return translateTopic(topic, this.translate);
	}

	translateSourceTypeLabel(source: string): string {
		return translateSourceType(source, this.translate);
	}

	// Short media-type badge (next to the full mediaTypeOptions label) and the outlet-type tag in
	// the Media Outlet picker both reuse MEDIUM.TYPES - mediaTypeShortLabel goes through
	// MEDIA_TYPE_TO_BACKEND first since this component's own ids ('Web'/'Print') differ from the
	// backend's ('WEB'/'PRINTED'), which is what mediumTypeLabel (and outlet.type) already use.
	mediaTypeShortLabel(id: string): string {
		return this.mediumTypeLabel(MEDIA_TYPE_TO_BACKEND[id]);
	}

	mediumTypeLabel(code: string | undefined): string {
		return code ? this.translate.instant('MEDIUM.TYPES.' + code) : '';
	}

	get brandRoleOptions(): { id: 'all' | 'client' | 'competition'; label: string }[] {
		const clientBrand = this.allBrands.find((b) => b.role === 'client');
		return [
			{ id: 'all', label: this.translate.instant('ANALYTICS_FILTER_BAR.ALL_BRANDS') },
			{
				id: 'client',
				label: clientBrand
					? this.translate.instant('ANALYTICS_FILTER_BAR.OUR_BRAND_WITH_NAME', { name: clientBrand.name })
					: this.translate.instant('ANALYTICS_GLOSSARY.OUR_BRAND'),
			},
			{ id: 'competition', label: this.translate.instant('ANALYTICS_GLOSSARY.COMPETITION') },
		];
	}

	get topicGroupOptions(): { id: 'all' | 'Industry' | 'Corporate'; label: string }[] {
		return [
			{ id: 'all', label: this.translate.instant('ANALYTICS_FILTER_BAR.ALL_GROUPS') },
			{ id: 'Industry', label: this.translate.instant('ANALYTICS_FILTER_BAR.INDUSTRY') },
			{ id: 'Corporate', label: this.translate.instant('ANALYTICS_FILTER_BAR.CORPORATE') },
		];
	}

	currentFilters: FilterState = { ...this.filters };
	drawerOpen = false;
	activeDropdown: 'brand' | null = null;
	draftFilters: FilterState = { ...this.filters };

	@ViewChild('dropdownRoot') dropdownRoot?: ElementRef<HTMLElement>;

	// Needed because brandOptions/filters.brands now arrive asynchronously (after the parent's
	// GET /v1/client/analytics/brands call resolves) - the field initializers above only ever see
	// the constructor-time default, so without this, a real brand list that loads after first
	// render would never reach currentFilters/draftFilters.
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.currentFilters = { ...this.filters };
			if (!this.drawerOpen) {
				this.draftFilters = { ...this.filters };
			}
		}
	}

	@HostListener('document:mousedown', ['$event'])
	onDocumentMousedown(event: MouseEvent): void {
		if (this.dropdownRoot && !this.dropdownRoot.nativeElement.contains(event.target as Node)) {
			this.activeDropdown = null;
		}
	}

	// --- Date range / compare (delegated to DateRangePickerComponent) ---

	get pickerDateRange(): DateRange {
		const fallback = currentQuarterRange();
		return {
			startDate: this.currentFilters.startDate || fallback.start,
			endDate: this.currentFilters.endDate || fallback.end,
			label: this.currentFilters.dateRange,
		};
	}

	get pickerCompareOption(): CompareOption {
		return {
			type: this.currentFilters.compareType || 'previous_period',
			label: this.currentFilters.comparePeriod,
			startDate: this.currentFilters.compareStartDate,
			endDate: this.currentFilters.compareEndDate,
		};
	}

	onDateRangeChange(newRange: DateRange): void {
		this.currentFilters = {
			...this.currentFilters,
			dateRange: newRange.label || `${formatDisplayDate(newRange.startDate, this.translate.currentLang)} – ${formatDisplayDate(newRange.endDate, this.translate.currentLang)}`,
			startDate: newRange.startDate,
			endDate: newRange.endDate,
			datePresetKey: newRange.presetKey,
		};
		this.filtersChange.emit(this.currentFilters);
	}

	onCompareOptionChange(newCompare: CompareOption): void {
		this.currentFilters = {
			...this.currentFilters,
			comparePeriod: newCompare.label,
			compareType: newCompare.type,
			compareStartDate: newCompare.startDate,
			compareEndDate: newCompare.endDate,
		};
		this.filtersChange.emit(this.currentFilters);
	}

	// --- Brand quick-select dropdown ---

	toggleBrandDropdown(): void {
		this.activeDropdown = this.activeDropdown === 'brand' ? null : 'brand';
	}

	toggleBrand(brandId: string): void {
		let updated: string[];
		if (this.currentFilters.brands.includes(brandId)) {
			if (this.currentFilters.brands.length === 1) return; // keep at least 1
			updated = this.currentFilters.brands.filter((b) => b !== brandId);
		} else {
			updated = [...this.currentFilters.brands, brandId];
		}
		this.currentFilters = { ...this.currentFilters, brands: updated };
		this.filtersChange.emit(this.currentFilters);
	}

	selectAllBrands(): void {
		this.currentFilters = { ...this.currentFilters, brands: this.allBrands.map((b) => b.id) };
		this.filtersChange.emit(this.currentFilters);
	}

	selectOnlyClient(): void {
		const clientBrand = this.allBrands.find((b) => b.role === 'client');
		if (!clientBrand) return;
		this.currentFilters = { ...this.currentFilters, brands: [clientBrand.id] };
		this.filtersChange.emit(this.currentFilters);
	}

	get clientBrandLabel(): string {
		return this.allBrands.find((b) => b.role === 'client')?.name || this.translate.instant('ANALYTICS_GLOSSARY.CLIENT');
	}

	get brandButtonLabel(): string {
		// Пакет А #5: brandOptions arrives asynchronously (AnalyticsPanelComponent's own
		// GET /v1/client/analytics/brands) - while it's still empty, `this.currentFilters.brands`
		// can already hold ids with nothing in `allBrands` to look them up against. Every branch
		// below depends on `allBrands` resolving an id to a name, so bail out to a loading
		// placeholder instead of falling through to BRAND_PLUS_N with an undefined name and a
		// negative count (brands.length - 1 when brands is still []).
		if (this.allBrands.length === 0) {
			return '…';
		}
		const brands = this.currentFilters.brands;
		const clientBrand = this.allBrands.find((b) => b.role === 'client');
		if (this.allBrands.length > 0 && brands.length === this.allBrands.length) {
			return this.translate.instant('ANALYTICS_FILTER_BAR.ALL_BRANDS_COUNT', { count: this.allBrands.length });
		}
		if (clientBrand && brands.length === 1 && brands[0] === clientBrand.id) {
			return this.translate.instant('ANALYTICS_FILTER_BAR.BRAND_WITH_OUR_BRAND_TAG', { name: clientBrand.name });
		}
		if (brands.length === 1) {
			const match = this.allBrands.find((b) => b.id === brands[0]);
			return match ? match.name : brands[0];
		}
		const first = this.allBrands.find((b) => b.id === brands[0]);
		return this.translate.instant('ANALYTICS_FILTER_BAR.BRAND_PLUS_N', { name: first ? first.name : brands[0], count: brands.length - 1 });
	}

	// --- Filters drawer ---

	get activeCount(): number {
		const f = this.currentFilters;
		let count = 0;
		// brandRole is intentionally excluded (2026-11-01): the section is hidden because the
		// filter has no effect on the backend request - see the @if (false) comment in the
		// template. Not counting it here means an old Saved View/URL that still carries
		// brandRole='client'/'competition' doesn't show a phantom "1 active filter" badge.
		count += f.mediaTypes.length;
		count += f.mediaOutlets.length;
		count += f.authors.length;
		if (f.topicGroup !== 'all') count += 1;
		count += f.topics.length;
		count += f.sourceTypes.length;
		count += f.sentiments.length;
		return count;
	}

	openDrawer(): void {
		this.draftFilters = { ...this.currentFilters };
		this.drawerOpen = true;
	}

	closeDrawer(): void {
		this.drawerOpen = false;
	}

	applyDrawerFilters(): void {
		this.currentFilters = this.draftFilters;
		this.filtersChange.emit(this.currentFilters);
		this.drawerOpen = false;
	}

	clearDrawerFilters(): void {
		this.draftFilters = {
			...this.draftFilters,
			brandRole: 'all',
			mediaTypes: [],
			mediaOutlets: [],
			authors: [],
			topicGroup: 'all',
			topics: [],
			sourceTypes: [],
			sentiments: [],
		};
	}

	setDraftBrandRole(role: 'all' | 'client' | 'competition'): void {
		this.draftFilters = { ...this.draftFilters, brandRole: role };
	}

	resetBrandRole(): void {
		this.draftFilters = { ...this.draftFilters, brandRole: 'all' };
	}

	toggleDraftMediaType(typeId: string): void {
		const updated = this.draftFilters.mediaTypes.includes(typeId)
			? this.draftFilters.mediaTypes.filter((t) => t !== typeId)
			: [...this.draftFilters.mediaTypes, typeId];
		// Same author-pruning reason as toggleDraftMediaOutlet - narrowing/widening the type
		// selection can leave previously-selected authors outside the new cascade.
		const prunedAuthors = this.draftFilters.authors.filter((id) => this.authorMatchesMediaSelection(id, { ...this.draftFilters, mediaTypes: updated }));
		this.draftFilters = { ...this.draftFilters, mediaTypes: updated, authors: prunedAuthors };
	}

	resetMediaSection(): void {
		this.draftFilters = { ...this.draftFilters, mediaTypes: [], mediaOutlets: [], authors: [] };
	}

	hasMediaSelections(): boolean {
		return this.draftFilters.mediaTypes.length > 0 || this.draftFilters.mediaOutlets.length > 0 || this.draftFilters.authors.length > 0;
	}

	// --- Media Outlet picker ---

	get filteredMediaOutlets(): ClientAnalyticsMediaOutletOption[] {
		const term = this.mediaOutletSearch.trim().toLowerCase();
		if (!term) return this.mediaOutletOptions;
		return this.mediaOutletOptions.filter((o) => (o.name || '').toLowerCase().includes(term));
	}

	toggleDraftMediaOutlet(outletId: string): void {
		const updated = this.draftFilters.mediaOutlets.includes(outletId)
			? this.draftFilters.mediaOutlets.filter((id) => id !== outletId)
			: [...this.draftFilters.mediaOutlets, outletId];
		// Dropping an outlet the author list was cascaded from can leave selected authors that no
		// longer match any eligible outlet - prune them so the two pickers never disagree silently.
		const prunedAuthors = this.draftFilters.authors.filter((id) => this.authorMatchesMediaSelection(id, { ...this.draftFilters, mediaOutlets: updated }));
		this.draftFilters = { ...this.draftFilters, mediaOutlets: updated, authors: prunedAuthors };
	}

	// --- Author picker (cascades from the selected media type(s) / outlet(s), same rule as before) ---

	// Specific outlets win over broad media types when both are set, since picking an outlet is
	// the more precise choice - same precedence the backend request itself doesn't need to encode
	// since it filters on whichever ids/types it's given independently.
	private eligibleOutletIdsForAuthorCascade(filters: FilterState): Set<string> | null {
		if (filters.mediaOutlets.length > 0) {
			return new Set(filters.mediaOutlets);
		}
		if (filters.mediaTypes.length > 0) {
			const backendTypes = new Set(filters.mediaTypes.map((t) => MEDIA_TYPE_TO_BACKEND[t]).filter(Boolean));
			return new Set(this.mediaOutletOptions.filter((o) => o.type && backendTypes.has(o.type)).map((o) => o.id!));
		}
		return null;
	}

	private authorMatchesMediaSelection(authorId: string, filters: FilterState): boolean {
		const eligibleOutletIds = this.eligibleOutletIdsForAuthorCascade(filters);
		if (!eligibleOutletIds) return true;
		const author = this.authorOptions.find((a) => a.id === authorId);
		return !!author?.mediumIds?.some((id) => eligibleOutletIds.has(id));
	}

	get cascadedAuthorOptions(): ClientAnalyticsAuthorOption[] {
		const eligibleOutletIds = this.eligibleOutletIdsForAuthorCascade(this.draftFilters);
		if (!eligibleOutletIds) return this.authorOptions;
		return this.authorOptions.filter((a) => a.mediumIds?.some((id) => eligibleOutletIds.has(id)));
	}

	get filteredAuthorOptions(): ClientAnalyticsAuthorOption[] {
		const term = this.authorSearch.trim().toLowerCase();
		const cascaded = this.cascadedAuthorOptions;
		if (!term) return cascaded;
		return cascaded.filter((a) => (a.name || '').toLowerCase().includes(term));
	}

	toggleDraftAuthor(authorId: string): void {
		const updated = this.draftFilters.authors.includes(authorId) ? this.draftFilters.authors.filter((id) => id !== authorId) : [...this.draftFilters.authors, authorId];
		this.draftFilters = { ...this.draftFilters, authors: updated };
	}

	setDraftTopicGroup(group: 'all' | 'Industry' | 'Corporate'): void {
		let prunedTopics = this.draftFilters.topics;
		if (group === 'Industry') {
			prunedTopics = this.draftFilters.topics.filter((t) => this.industryTopics.includes(t));
		} else if (group === 'Corporate') {
			prunedTopics = this.draftFilters.topics.filter((t) => this.corporateTopics.includes(t));
		}
		this.draftFilters = { ...this.draftFilters, topicGroup: group, topics: prunedTopics };
	}

	resetContentSection(): void {
		this.draftFilters = { ...this.draftFilters, topicGroup: 'all', topics: [], sourceTypes: [] };
	}

	get topicCascadeFromLabel(): string {
		const group = this.draftFilters.topicGroup;
		const groupLabel =
			group === 'all'
				? this.translate.instant('ANALYTICS_FILTER_BAR.ALL_GROUPS_INLINE')
				: this.translate.instant(`ANALYTICS_FILTER_BAR.${group.toUpperCase()}`);
		return this.translate.instant('ANALYTICS_FILTER_BAR.TOPIC_CASCADES_FROM', { group: groupLabel });
	}

	get availableTopics(): string[] {
		if (this.draftFilters.topicGroup === 'Industry') return this.industryTopics;
		if (this.draftFilters.topicGroup === 'Corporate') return this.corporateTopics;
		return [...this.industryTopics, ...this.corporateTopics];
	}

	isIndustryTopic(topic: string): boolean {
		return this.industryTopics.includes(topic);
	}

	toggleDraftTopic(topic: string): void {
		const updated = this.draftFilters.topics.includes(topic) ? this.draftFilters.topics.filter((t) => t !== topic) : [...this.draftFilters.topics, topic];
		this.draftFilters = { ...this.draftFilters, topics: updated };
	}

	toggleDraftSourceType(source: string): void {
		const updated = this.draftFilters.sourceTypes.includes(source)
			? this.draftFilters.sourceTypes.filter((s) => s !== source)
			: [...this.draftFilters.sourceTypes, source];
		this.draftFilters = { ...this.draftFilters, sourceTypes: updated };
	}

	resetSentiments(): void {
		this.draftFilters = { ...this.draftFilters, sentiments: [] };
	}

	toggleDraftSentiment(id: string): void {
		const updated = this.draftFilters.sentiments.includes(id) ? this.draftFilters.sentiments.filter((s) => s !== id) : [...this.draftFilters.sentiments, id];
		this.draftFilters = { ...this.draftFilters, sentiments: updated };
	}

	// SETTE redesign (CLIPPING-598), Правило А: media type is always MEDIA_COLORS, same tint
	// tokens used on the Media screen - never an arbitrary categorical color per type.
	mediaTypeBadgeClasses(type: string): string {
		switch (type) {
			case 'TV':
				return 'bg-media-tint-tv text-media-tv';
			case 'Web':
				return 'bg-media-tint-portal text-media-portal';
			case 'Print':
				return 'bg-media-tint-print text-media-print';
			case 'Radio':
				return 'bg-media-tint-radio text-media-radio';
			case 'Social':
				return 'bg-media-tint-social text-media-social';
			default:
				return 'bg-surface-gray text-ink-muted';
		}
	}

}
